import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {ApiBaseService} from "../../../../../../../projects/vendors/src/lib/services/api-base.service";
import {FormHelper, KeyString, KeyValue} from "../../../../../../../projects/vendors/src/lib/services/form-helper";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ItemCategory} from "../../../../../@domain/item-category";
import {Menu} from "../../../../../@domain/menu";
import {ApiEnum} from "../../../../../@application/enum/api.enum";
import {FoodItemQuantity} from "../../../../../@domain/food-item-quantity";
import {FoodItemSearchTag} from "../../../../../@domain/food-item-search-tag";
import {FOOD_SECTIONS} from "../../../../../@application/common/food-section";
import {Editor, Toolbar} from 'ngx-editor';

@Component({
    selector: 'app-add-new-food',
    templateUrl: './add-new-food.component.html',
    styleUrl: './add-new-food.component.scss',
    standalone: false
})
export class AddNewFoodComponent implements OnInit, OnDestroy {
  foodItemForm!: FormGroup;
  apiBaseService = inject(ApiBaseService);
  private dialogRef = inject(MatDialogRef<AddNewFoodComponent>)

  quantityTagKeyValue = [] as KeyString[]
  itemCategoryKeyValue = [] as KeyString[]
  searchTagKeyValue = [] as KeyString[]
  kitchenKeyValue = [] as KeyString[]
  menuKeyValue = [] as KeyString[]
  imageUrl: string = 'https://img.freepik.com/free-psd/3d-icon-with-traditional-colombian-representation_23-2150738031.jpg?semt=ais_hybrid&w=740';
  currentSection = 'Basic';
  foodName!: string
  foodPrice!: number
  description!: string;
  itemCode!: string;
  foodSection = FOOD_SECTIONS;
  isVegetarian = false
  isNonVegetarian = false
  status = true;
  priceRange = [] as { size: string; price: number, id: string }[]
  ingredientKeyValue = [] as KeyString[]
  macrosExpanded = false;

  // Static option arrays
  spicyLevelOptions: KeyValue[] = [
    {key: 0, value: 'None'},
    {key: 1, value: 'Mild'},
    {key: 2, value: 'Medium'},
    {key: 3, value: 'Hot'},
    {key: 4, value: 'Extra Hot'}
  ];

  satietyLevelOptions: KeyValue[] = [
    {key: 1, value: 'Low'},
    {key: 2, value: 'Medium'},
    {key: 3, value: 'High'}
  ];

  dietaryAttributeOptions: KeyValue[] = [
    {key: 1, value: 'Vegetarian'},
    {key: 2, value: 'Vegan'},
    {key: 3, value: 'Gluten Free'},
    {key: 4, value: 'Dairy Free'},
    {key: 5, value: 'Halal'},
    {key: 6, value: 'Nut Free'}
  ];

  // Rich text editor
  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic', 'underline'],
    ['bullet_list', 'ordered_list'],
    ['link'],
    ['undo', 'redo']
  ];

  data = inject<{
    menuId: string,
    categoryId: string,
    categories: ItemCategory[],
    foodItemQuantity:FoodItemQuantity[]}>(MAT_DIALOG_DATA)


  statusKeyValue = FormHelper.GetFoodStatusList();
  foodCookTypeKeyValue = FormHelper.GetFoodCookTypeList();
  preparationTypeKeyValue = FormHelper.GetPreparationTypeList();

  ngOnInit(): void {
    this.editor = new Editor();
    this.formInit()
    this.getFoodItemSearchTag()
    this.getIngredients()
    this.loadMenus()
    this.getKitchen()
    this.setDropDown();
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  private setDropDown(){
    this.itemCategoryKeyValue = FormHelper.ConvertForDropDown(this.data.categories, {
      key: 'id',
      value: 'name'
    });
    this.quantityTagKeyValue = FormHelper.ConvertForDropDown(this.data.foodItemQuantity, {
      key: 'id',
      value: 'name'
    });
  }

  formInit() {
    this.foodItemForm = new FormGroup({
      name: new FormControl(null, [Validators.required]),
      description: new FormControl(null),
      imageUrl: new FormArray([]),
      videoUrl: new FormControl(null),
      foodCookType: new FormControl(null, [Validators.required]),
      preparationType: new FormControl(null, [Validators.required]),
      dailyRequirement: new FormControl(0, [Validators.required]),
      status: new FormControl(null, [Validators.required]),
      basePrice: new FormControl(null, [Validators.required]),
      foodCategoryId: new FormControl(this.data.categoryId, [Validators.required]),
      foodQuantityTags: new FormArray([]),
      foodSearchTagIds: new FormControl(null),
      kitchenId: new FormControl(null, [Validators.required]),
      foodItemCode: new FormControl(null, [Validators.required]),
      preparationTime: new FormControl(null),
      origin: new FormControl(null),
      cookingMethod: new FormControl(null),
      basicIngredients: new FormControl(null),
      allergens: new FormControl(null),
      menuIds: new FormControl([this.data.menuId], [Validators.required]),
      materialCost: new FormControl(0),
      overheadCost: new FormControl(0),
      spicyLevel: new FormControl(0),
      satietyLevel: new FormControl(null),
      feedbackPercentage: new FormControl(null, [Validators.min(0), Validators.max(100)]),
      dietaryAttributes: new FormControl([]),
      foodIngredientIds: new FormControl([]),
      macros: new FormGroup({
        calories: new FormControl(null),
        protein: new FormControl(null),
        carbohydrates: new FormControl(null),
        fat: new FormControl(null),
        fiber: new FormControl(null),
        sugar: new FormControl(null),
        sodium: new FormControl(null),
      }),
    });
    this.onAddImage();
    this.setDefaultValues();
    this.onAddFoodQuantityTag()
    this.foodItemForm.get('name')?.valueChanges.subscribe({
      next: value => {
        this.foodName = value;
      }
    })
    this.foodItemForm.get('basePrice')?.valueChanges.subscribe({
      next: value => {
        this.foodPrice = value;
        this.foodQuantityTags.controls.forEach(control => {
          const additionalPrice = control.get('additionalPrice')?.value || 0;
          control.get('totalPrice')?.setValue(value + additionalPrice);
        });
      }
    })
    this.foodItemForm.get('description')?.valueChanges.subscribe({
      next: value => { this.description = value?.replace(/<[^>]*>/g, '') || ''; }
    })
    this.foodItemForm.get('foodItemCode')?.valueChanges.subscribe({
      next: value => {
        this.itemCode = value;
      }
    })

    this.foodItemForm.get('dietaryAttributes')?.valueChanges.subscribe({
      next: value => {
        this.setVegetarianNonVegetarian(value);
      }
    })
  }

  private setDefaultValues() {
    this.foodItemForm.patchValue({
      foodCookType: this.foodCookTypeKeyValue[0]?.key,
      preparationType: this.preparationTypeKeyValue[0]?.key,
      status: this.statusKeyValue[0]?.key,
      kitchenId: this.kitchenKeyValue[0]?.key
    });
  }

  private setVegetarianNonVegetarian(dietaryAttributes: number[]) {
    // 1 = Vegetarian, 2 = Vegan
    this.isVegetarian = dietaryAttributes?.some(attr => attr === 1 || attr === 2) ?? false;
    this.isNonVegetarian = !this.isVegetarian && (dietaryAttributes?.length > 0);
  }
  onPrice($event: Event, i: number, qtyTagId: string) {
    const inputElement = $event.target as HTMLInputElement;
    const value = parseFloat(inputElement.value);
    if (!isNaN(value)) {
      this.foodQuantityTags.at(i).get('additionalPrice')?.setValue(value);
      this.foodQuantityTags.at(i).get('totalPrice')?.setValue(value + this.foodPrice);
    } else {
      this.foodQuantityTags.at(i).get('additionalPrice')?.setValue(0);
      this.foodQuantityTags.at(i).get('totalPrice')?.setValue(this.foodPrice);
    }
    this.setPrice(qtyTagId, this.foodQuantityTags.at(i).get('totalPrice')?.value || 0);
    this.addPriceIfNotExists(qtyTagId, this.foodQuantityTags.at(i).get('totalPrice')?.value || 0);
  }

  private addPriceIfNotExists(qtyTagId: string, price: number) {
    const existingTag = this.priceRange.find(item => item.id === qtyTagId);
    const size = this.data.foodItemQuantity.find(item => item.id === qtyTagId)?.name.charAt(0).toUpperCase() || '';
    if (!existingTag) {
      this.priceRange.push({id: qtyTagId, size: size, price});
    }
    this.priceRange = this.priceRange.sort((a, b) => a.price - b.price);
  }

  private setPrice(qtyTagId: string, price: number) {
    const existingTag = this.priceRange.find(item => item.id === qtyTagId);
    if (existingTag) {
      existingTag.price = price;
    }
  }

  get imageUrls(): FormArray {
    return this.foodItemForm.get('imageUrl') as FormArray;
  }

  onAddImage(): void {
    this.imageUrls.push(new FormGroup({
      image: new FormControl(null)
    }));
  }

  onSectionChange(sectionId: string): void {
    this.currentSection = sectionId;
  }

  scrollTo(section: string): void {
    document.getElementById(section)?.scrollIntoView();
    this.currentSection = section;
  }

  onMouseMove(event: MouseEvent, card: HTMLElement) {
    const rect = card.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = -(y - centerY) / 15;
    const rotateY = (x - centerX) / 15;

    card.style.transform = `perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.03)`;
    card.classList.add('pop');
  }

  onMouseLeave(card: HTMLElement) {
    card.style.transform = `perspective(600px) rotateX(0deg) rotateY(0deg) scale(1)`;
    card.classList.remove('pop');
  }

  get foodQuantityTags(): FormArray {
    return this.foodItemForm.get('foodQuantityTags') as FormArray;
  }

  onAddFoodQuantityTag(): void {
    if (this.foodQuantityTags.length < this.data.foodItemQuantity.length) {
      const group = new FormGroup({
        foodQuantityTagId: new FormControl(null, [Validators.required]),
        additionalPrice: new FormControl(0, [Validators.required]),
        additionalCost: new FormControl(0, [Validators.required]),
        totalPrice: new FormControl(null)
      });
      this.foodQuantityTags.push(group);
      group.get('foodQuantityTagId')?.valueChanges
        .subscribe({
          next: values => {
            this.updateAvailableItemsForDropdown();
            this.addPriceIfNotExists(values!, this.foodPrice);
          }
        })
    }
  }

  onDeleteFoodQuantityTag(index: number) {
    this.foodQuantityTags.removeAt(index);
    this.updateAvailableItemsForDropdown();
  }

  private updateAvailableItemsForDropdown() {
    const selectedTagIds = this.foodQuantityTags.controls
      .map(control => control.get('foodQuantityTagId')?.value)
    this.quantityTagKeyValue = FormHelper.ConvertForDropDown(
      this.data.foodItemQuantity.filter(item => !selectedTagIds.includes(item.id)),
      {key: 'id', value: 'name'}
    );
  }

  onSubmit() {
    if (this.foodItemForm.valid) {
      const formValue = {...this.foodItemForm.value};
      const imageUrlArray = formValue.imageUrl.map((item: { image: string }) => item.image);
      formValue.imageUrl = imageUrlArray.join(', ') == '' ? this.imageUrl : imageUrlArray.join(', ');
      formValue.preparationTime = formValue.preparationTime == null ? null : formValue.preparationTime.toString();
      this.createFoodItem(formValue)

    } else {
     this.markFormGroupTouched(this.foodItemForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormControl) {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({onlySelf: true});
        }
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        // Recursively mark nested FormGroup or FormArray
        this.markFormGroupTouched(control);
      }
    });
  }

  private createFoodItem(data: any) {
    this.apiBaseService.POST_API([ApiEnum.FoodModule, ApiEnum.Food], data)
      .subscribe({
        next: value => {
          if (value != null) {
            this.dialogRef.close(value);
          }
        }
      });
  }

  getFoodItemSearchTag(): void {
    this.apiBaseService.GET<FoodItemSearchTag[]>([ApiEnum.FoodModule, ApiEnum.FoodItemSearchTag])
      .subscribe(res => {
        this.searchTagKeyValue = FormHelper.ConvertForDropDown(res, {
          key: 'id',
          value: 'name'
        });
      });
  }

  private loadMenus() {
    this.apiBaseService.GET<Menu[]>([ApiEnum.FoodModule, ApiEnum.Menu]).subscribe({
      next: response => {
        this.menuKeyValue = FormHelper.ConvertForDropDown(response, {
          key: 'id',
          value: 'name'
        });
      }
    });
  }

  getKitchen(): void {
    this.apiBaseService.GET<any[]>([ApiEnum.RestaurantModule, ApiEnum.Kitchen])
      .subscribe(res => {
        this.kitchenKeyValue = FormHelper.ConvertForDropDown(res, {
          key: 'id',
          value: 'name'
        });
        this.foodItemForm.patchValue({
          kitchenId: this.kitchenKeyValue[0]?.key
        });
      });
  }

  getSpicyLevelClass(level: number): string {
    switch (level) {
      case 0: return 'spicy-none';
      case 1: return 'spicy-mild';
      case 2: return 'spicy-medium';
      case 3: return 'spicy-hot';
      case 4: return 'spicy-extra-hot';
      default: return 'spicy-none';
    }
  }

  get totalCost(): number {
    const material = this.foodItemForm?.get('materialCost')?.value || 0;
    const overhead = this.foodItemForm?.get('overheadCost')?.value || 0;
    return material + overhead;
  }

  get marginPercentage(): number {
    const basePrice = this.foodItemForm?.get('basePrice')?.value || 0;
    if (basePrice <= 0) return 0;
    return ((basePrice - this.totalCost) / basePrice) * 100;
  }

  private getIngredients(): void {
    this.apiBaseService.GET<any[]>([ApiEnum.FoodModule, ApiEnum.FoodIngredient])
      .subscribe(res => {
        this.ingredientKeyValue = FormHelper.ConvertForDropDown(res, {
          key: 'id',
          value: 'name'
        });
      });
  }

    protected readonly foodSections = FOOD_SECTIONS;
}
